import { DesignSystemToken, TokenValue } from '../types';

// Token registry - maps CSS variable names to token values
let tokenRegistry: Map<string, DesignSystemToken> = new Map();

// Update the token registry with new tokens
export function updateTokenRegistry(tokens: DesignSystemToken[]): void {
	tokenRegistry.clear();
	for (const token of tokens) {
		tokenRegistry.set(token.cssVariable, token);
	}
}

// Update a single token in the registry
export function updateToken(token: DesignSystemToken): void {
	tokenRegistry.set(token.cssVariable, token);
	// Regenerate CSS if style element exists
	const styleElement = document.getElementById('design-system-tokens') as HTMLStyleElement;
	if (styleElement) {
		// Rebuild CSS from all tokens in registry
		const tokens = Array.from(tokenRegistry.values());
		styleElement.textContent = generateTokensCSS(tokens);
	}
}

// Get token value from registry
export function getToken(cssVariable: string): DesignSystemToken | undefined {
	return tokenRegistry.get(cssVariable);
}

// Convert token value to CSS-compatible string
function tokenValueToCSS(value: TokenValue): string {
	switch (value.kind) {
		case 'color':
			return value.hex;
		case 'length':
			if (value.px !== undefined) {
				return `${value.px}px`;
			}
			if (value.rem !== undefined) {
				return value.rem;
			}
			if (value.em !== undefined) {
				return value.em;
			}
			return value.original;
		case 'shadow':
			return `${value.offsetX}px ${value.offsetY}px ${value.blur}px ${
				value.spread || 0
			}px ${tokenValueToCSS(value.color)}`;
		case 'fontFamily':
			return value.families.join(', ');
		case 'fontSize':
			return tokenValueToCSS(value.length);
		case 'lineHeight':
			if (value.numeric !== undefined) {
				return value.numeric.toString();
			}
			if (value.length) {
				return tokenValueToCSS(value.length);
			}
			return value.original;
		case 'fontWeight':
			return value.numeric.toString();
		case 'borderRadius':
			return tokenValueToCSS(value.length);
		case 'opacity':
			return value.value.toString();
		case 'transition':
			const property = value.property || 'all';
			const delay = value.delay || '0s';
			return `${property} ${value.duration} ${value.timingFunction} ${delay}`;
		case 'number':
			return value.value.toString();
		case 'string':
		default:
			return value.original;
	}
}

// Resolve a CSS value that may contain var(--*) references
// Handles nested var() calls and multiple references in a single value
export function resolveTokenValue(cssValue: string): string {
	// Match var(--*) patterns for any CSS custom property (--ds-*, --theme-*, etc.)
	const varRegex = /var\((--[^,)]+)\)/g;

	// Keep resolving until no more var() references are found (handles nested/recursive cases)
	let result = cssValue;
	let previousResult = '';
	let iterations = 0;
	const maxIterations = 10; // Prevent infinite loops

	while (result !== previousResult && iterations < maxIterations) {
		previousResult = result;
		result = result.replace(varRegex, (match, varContent) => {
			// Extract only the CSS custom property name (before optional comma/fallback)
			const cssVariable = varContent.split(',')[0].trim();
			const token = tokenRegistry.get(cssVariable);
			if (token) {
				const resolved = tokenValueToCSS(token.value);
				// If the resolved value still contains var(), it will be resolved in next iteration
				return resolved;
			}
			// If token not found, return the original var() reference
			return match;
		});
		iterations++;
	}

	return result;
}

// Generate CSS custom properties string from tokens
export function generateTokensCSS(tokens: DesignSystemToken[]): string {
	const lines: string[] = [];
	lines.push(':root {');

	for (const token of tokens) {
		// Use the original CSS value from the token
		const cssValue = token.value.original;
		lines.push(`  ${token.cssVariable}: ${cssValue};`);
	}

	lines.push('}');
	return lines.join('\n');
}
